/**
 * Application-level configuration
 * Shared constants used across frontend and backend
 */

/** Public base URL for the Suikamon application */
export const SUIKAMON_PUBLIC_URL = 'https://suikamon.com';

/**
 * Default backend API base URL.
 *
 * Used by the web app when neither `VITE_API_URL` nor `VITE_API_BASE_URL` is set at build
 * time. It is the same value `suikamon-web/Dockerfile` bakes into `ARG VITE_API_URL`, so a
 * bundle built without the env var talks to the backend the Docker image would have used
 * instead of shipping a broken app.
 */
export const SUIKAMON_API_URL = 'https://suikamon-backend.onrender.com';

/**
 * The one address a player (or a regulator) can write to about their data or the rules.
 * Suikamon is a hobby project run by an independent developer — there is no company — so the
 * legal pages name this inbox, not a firm. It is also the Google sign-in "support email".
 */
export const SUIKAMON_CONTACT_EMAIL = 'suikamongame@gmail.com';

/** When the Terms / Privacy text last changed. A fixed date — never `new Date()`, which made the
 *  pages claim they were rewritten on the day you read them. Move it when the text changes. */
export const LEGAL_LAST_UPDATED = 'September 26, 2026';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * THE RELEASE NUMBER — one number, everywhere, and the ONLY place it is written.
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Four things carry the version, and every one of them is this number:
 *
 * | surface                    | reads              | example  |
 * |----------------------------|--------------------|----------|
 * | home badge, backend log    | `SUIKAMON_APP_VERSION` | `Beta 13.0` |
 * | news + changelog headers   | `CURRENT_SEASON` (gameConstants) | `Season 13.0` |
 * | newest `ChangelogPage` entry | checked, not derived (it is data) | `13.0` |
 * | all three `package.json`   | checked, not derived (JSON cannot import TS) | `13.0.x` |
 *
 * **They ran on separate counters until release 13** — home said `Beta 12.25`, the season said
 * `Season 6.0`, the changelog had reached `12.99` and the packages were at `5.9.1`. Four numbers
 * for one thing, so "what version am I on?" had no answer. The guard that existed compared only
 * the MAJORS of two of the four, which is exactly why the other two drifted unseen.
 *
 * **What is derived cannot drift; what cannot be derived is checked by the build.**
 * `scripts/check-versions.mjs` (wired into both `npm run build` and `npm test`) and
 * `shared/domain/__tests__/releaseVersion.test.ts` fail if any of the four disagrees.
 *
 * ── Bumping it ──
 * Bump `SUIKAMON_RELEASE_PATCH` with every changelog entry: the newest entry must read exactly
 * `${SUIKAMON_RELEASE}.${SUIKAMON_RELEASE_PATCH}`. Bump `SUIKAMON_RELEASE` for a new release,
 * reset the patch to 0, and bump all three package.json majors to match in the same change.
 * The `release-ready` skill walks it.
 */
export const SUIKAMON_RELEASE = 21;

/**
 * Patch notes shipped inside the current release — the `.Y` of `Beta 13.Y`. It counts
 * `ChangelogPage` entries, and the build fails unless it equals the newest entry's minor.
 */
export const SUIKAMON_RELEASE_PATCH = 86;

/**
 * Human-facing application version — the home page badge, the backend's Telegram
 * `✅ Backend ready` line, the crash report, and `version.json`.
 *
 * Derived, never typed out: a literal here is what drifted to `Beta 12.25` against a changelog
 * at `12.99`.
 */
export const SUIKAMON_APP_VERSION = `Beta ${SUIKAMON_RELEASE}.${SUIKAMON_RELEASE_PATCH}`;

/**
 * The build a battle was fought on, as it is STAMPED ON THE ROW (`pvp_battles.game_version`,
 * `pvp_squad_battles.game_version`) — the bare number, not the `Beta ` prefix, so it matches
 * `balance_version`'s shape and the audit's numeric version ordering sorts both the same way.
 *
 * **It is not the same question as `BALANCE_VERSION`, which is why both are recorded.**
 * The balance stamp says which RULES a battle was fought under and deliberately holds still
 * across patch notes so samples stay big enough to measure. This says which BUILD it ran on.
 * When a player reports something odd, the build is the thing that identifies the deploy — and
 * a bug shipped in `13.4` and fixed in `13.6` is invisible to a stamp that reads `13.0` for both.
 */
export const GAME_VERSION = `${SUIKAMON_RELEASE}.${SUIKAMON_RELEASE_PATCH}`;
